import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import Navigation from './components/Navigation';
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import MasjidPage from './pages/MasjidPage';
import ClassesPage from './pages/ClassesPage';
import ContactPage from './pages/ContactPage';
import PrivacyPage from './pages/PrivacyPage';
import TimelinePage from './pages/TimelinePage';
import ShareRedirectPage from './pages/ShareRedirectPage';
import NotFoundPage from './pages/NotFoundPage';
import DashboardLayout from './layouts/DashboardLayout';
import DashboardOverviewPage from './pages/dashboard/DashboardOverviewPage';
import EventsPage from './pages/dashboard/EventsPage';
import ProjectsPage from './pages/dashboard/ProjectsPage';
import UsersPage from './pages/dashboard/UsersPage';
import CommitteePage from './pages/dashboard/CommitteePage';
import RolesPage from './pages/dashboard/RolesPage';
import NoticesPage from './pages/dashboard/NoticesPage';
import ClassesManagementPage from './pages/dashboard/ClassesManagementPage';
import GalleryPage from './pages/dashboard/GalleryPage';
import ContactDetailsPage from './pages/dashboard/ContactDetailsPage';
import DonorSectionPage from './pages/dashboard/DonorSectionPage';
import TimelineManagementPage from './pages/dashboard/TimelineManagementPage';
import DashboardSettingsPage from './pages/dashboard/DashboardSettingsPage';
import RolePermissionsPage from './pages/dashboard/RolePermissionsPage';
import SystemHealthPage from './pages/SystemHealthPage';
import TrafficAnalyticsPage from './pages/dashboard/TrafficAnalyticsPage';
import ReviewsPage from './pages/dashboard/ReviewsPage';
import GlobalLoader from './components/GlobalLoader';
import CookieConsentBanner from './components/CookieConsentBanner';
import ScrollToTop from './components/ScrollToTop';
import TrailingSlashRedirect from './components/TrailingSlashRedirect';
import { TelemetryTracker } from './features/telemetry';
import { SeoHead } from './shared/seo';
import './App.css';

/** Renders the public site navbar only when not on a dashboard route. */
const AppNav: React.FC = () => {
  const location = useLocation();
  if (location.pathname.startsWith('/dashboard')) return null;
  return <Navigation />;
};

const App: React.FC = () => {
  const { user, profile, isLoading } = useAuth();

  if (isLoading) {
    return <GlobalLoader />;
  }

  return (
    <BrowserRouter>
      <ScrollToTop />
      <TrailingSlashRedirect />
      <SeoHead />
      <div className="App">
        <TelemetryTracker />
        <AppNav />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/masjid" element={<MasjidPage />} />
          <Route path="/classes" element={<ClassesPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/timeline" element={<TimelinePage />} />
          <Route path="/our-target" element={<Navigate to="/#our-target" replace />} />
          <Route path="/share/:type/:id" element={<ShareRedirectPage />} />
          <Route path="/dashboard" element={user && profile ? <DashboardLayout /> : <Navigate to="/" replace />}>
            <Route index element={<DashboardOverviewPage />} />
            <Route path="events" element={<EventsPage />} />
            <Route path="projects" element={<ProjectsPage />} />
            <Route path="committee" element={<CommitteePage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="roles" element={<RolesPage />} />
            <Route path="notices" element={<NoticesPage />} />
            <Route path="classes" element={<ClassesManagementPage />} />
            <Route path="gallery" element={<GalleryPage />} />
            <Route path="reviews" element={<ReviewsPage />} />
            <Route path="contact-details" element={<ContactDetailsPage />} />
            <Route path="donors" element={<DonorSectionPage />} />
            <Route path="timeline" element={<TimelineManagementPage />} />
            <Route path="settings" element={<DashboardSettingsPage />} />
            <Route path="settings/permissions" element={<RolePermissionsPage />} />
            <Route path="system-health" element={<SystemHealthPage />} />
            <Route path="traffic" element={<TrafficAnalyticsPage />} />
          </Route>
          <Route path="*" element={<NotFoundPage />} />
        </Routes>

        <CookieConsentBanner />
      </div>
    </BrowserRouter>
  );
};

export default App;
